import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Lock, Users } from "lucide-react";
import { resolveEatzyMediaUrl } from "@/lib/utils";
import { AuthenticatedAvatarImage } from "@/components/AuthenticatedAvatarImage";
import FollowButton from "./FollowButton";

interface User {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  trustTier: string | null;
  reviewsCount: number | null;
}

interface FollowListResponse {
  users: User[];
  total: number;
  canView: boolean;
}

interface FollowersListModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialTab?: "followers" | "following";
  followersCount?: number;
  followingCount?: number;
}

export default function FollowersListModal({
  isOpen,
  onClose,
  userId,
  initialTab = "followers",
  followersCount = 0,
  followingCount = 0,
}: FollowersListModalProps) {
  const { isAuthenticated, user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);

  const { data: followersData, isLoading: isLoadingFollowers } = useQuery<FollowListResponse>({
    queryKey: ['/api/users', userId, 'followers'],
    queryFn: async () => {
      const headers: HeadersInit = {};
      if (isAuthenticated) {
        const _tok = localStorage.getItem('token');
        if (_tok && _tok !== 'null' && _tok !== 'undefined') {
          headers['Authorization'] = `Bearer ${_tok}`;
        }
      }
      const response = await fetch(`/api/users/${userId}/followers`, { headers });
      if (!response.ok) throw new Error('Failed to fetch followers');
      return response.json();
    },
    enabled: isOpen && activeTab === "followers",
  });

  const { data: followingData, isLoading: isLoadingFollowing } = useQuery<FollowListResponse>({
    queryKey: ['/api/users', userId, 'following'],
    queryFn: async () => {
      const headers: HeadersInit = {};
      if (isAuthenticated) {
        const _tok = localStorage.getItem('token');
        if (_tok && _tok !== 'null' && _tok !== 'undefined') {
          headers['Authorization'] = `Bearer ${_tok}`;
        }
      }
      const response = await fetch(`/api/users/${userId}/following`, { headers });
      if (!response.ok) throw new Error('Failed to fetch following');
      return response.json();
    },
    enabled: isOpen && activeTab === "following",
  });

  const renderUserList = (data: FollowListResponse | undefined, isLoading: boolean) => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (!data?.canView) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
          <Lock className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium text-foreground mb-2">This list is private</p>
          <p className="text-sm text-muted-foreground">
            Only the account owner can see their {activeTab} list
          </p>
        </div>
      );
    }

    if (!data?.users || data.users.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium text-foreground mb-2">
            {activeTab === "followers" ? "No followers yet" : "Not following anyone yet"}
          </p>
          <p className="text-sm text-muted-foreground">
            {activeTab === "followers"
              ? "When people follow this user, they'll appear here"
              : "When this user follows others, they'll appear here"}
          </p>
        </div>
      );
    }

    return (
      <ScrollArea className="h-[400px]">
        <div className="space-y-2 p-1">
          {data.users.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between p-3 rounded-lg hover-elevate transition-colors"
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AuthenticatedAvatarImage src={resolveEatzyMediaUrl(u.avatar) || undefined} alt={u.displayName || u.username} />
                  <AvatarFallback>
                    {(u.displayName || u.username || "U")[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-foreground">
                    {u.displayName || u.username}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    @{u.username} {u.reviewsCount ? `• ${u.reviewsCount} reviews` : ""}
                  </p>
                </div>
              </div>
              {u.id !== user?.id && (
                <FollowButton targetUserId={u.id} size="sm" />
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connections</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "followers" | "following")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="followers">
              Followers ({followersCount})
            </TabsTrigger>
            <TabsTrigger value="following">
              Following ({followingCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="followers" className="mt-4">
            {renderUserList(followersData, isLoadingFollowers)}
          </TabsContent>

          <TabsContent value="following" className="mt-4">
            {renderUserList(followingData, isLoadingFollowing)}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
